import { differenceInYears } from 'date-fns/differenceInYears'
import * as z from 'zod'

import { EUserRole } from '@/hooks/usePermissions/permissions.type'

/**
 * ////////////////////////////////
 * General Regex
 * ////////////////////////////////
 */
const emailRegex = /^(\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+)?$/

const nameRegex = /^(?!.*([áéíóúÁÉÍÓÚñÑ'’`])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-])+$/

const middleNameRegex =
  /^(?!.*([áéíóúÁÉÍÓÚñÑ'’`])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]*)$/

const alpanumericWithSpaces = /^([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'-])*$/

const alpanumericWithSpacesAndPoints =
  /^(?!.*([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'-]*(\.[^.\s]*){0,2})*$/

const addressRegex =
  /^(?!.*([áéíóúÁÉÍÓÚüÜñÑ])\1{2,})([\w\d\s.,\-áéíóúÁÉÍÓÚüÜñÑ]+)$/

const passwordRegex =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&_*-]).{8,}$/

const cityRegex =
  /^(?!.*([a-zA-ZáéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ\s.']*)$/
const state = /^(?!.*([a-zA-ZáéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ ,.'-]*)$/

const phoneOptional = /^(.*)$/

const organizationName =
  /^(?!.*([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])\1{2,})([A-Za-z0-9áéíóúÁÉÍÓÚñÑ_.,;:¡!¿?"'@#~$%&*()\-+=\[\]{}<>\s]*)$/

const optionalCollageRegex =
  /^(?!.*([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s,'-]{1,})?$/

const websiteRegex =
  /^(https?:\/\/)?([\w\-]+\.)+[\w\-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$|^$/

/* const signUpPhoneNumberRegex = /^[\+]?[1-9][\d]{0,15}$/ */

/**
 * ////////////////////////////////
 * Social Media Regex
 * ////////////////////////////////
 */
export const linkedInUrlRegex =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[A-Za-z0-9._-]+\/?$/

export const linkedInCompanyUrlRegex =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/(company|school|organization)\/[a-zA-Z0-9-_/]*$/

export const facebookUrlRegex =
  /^(https?:\/\/)?(www\.)?facebook\.com\/(profile\.php\?id=\d+|share\/[A-Za-z0-9_-]+|p\/[A-Za-z0-9._-]+|[A-Za-z0-9._-]+)(\/?(\?[^\s]*)?)?$/

export const twitterUrlRegex =
  /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9._-]+\/?$/

export const instagramUrlRegex =
  /^(https?:\/\/)?(www\.)?instagram\.com\/[A-Za-z0-9._-]+\/?$/

export const tikTokUrlRegex =
  /^(https?:\/\/)?(www\.)?tiktok\.com\/@[A-Za-z0-9._-]+\/?$/

export const pinterestUrlRegex =
  /^(https?:\/\/)?(www\.|[a-z]{2}\.)?pinterest\.com\/[A-Za-z0-9._-]+\/?$/

export const redditUrlRegex =
  /^(https?:\/\/)?(www\.)?reddit\.com\/user\/[A-Za-z0-9._-]+\/?$/

/**
 * ////////////////////////////////
 * Validations Functions
 * ////////////////////////////////
 */
const roleValidation = z.enum([EUserRole.OWNER, EUserRole.READER], {
  errorMap: () => ({ message: 'feedback.validations.invalidRole' }),
})

const pdfLanguageValidation = z
  .string()
  .min(1, { message: 'feedback.validations.pdfLanguageValidation' })

const emailValidation = z
  .string()
  .min(1, { message: 'feedback.validations.emailMinValidation' })
  .regex(emailRegex, 'feedback.validations.emailRegexValidation')

const creditsValidation = z.number()

const passwordValidation = z
  .string()
  .min(8, { message: 'feedback.validations.passwordMinValidation' })
  .max(128, {
    message: 'feedback.validations.passwordMaxValidation',
  })
  .regex(passwordRegex, 'feedback.validations.passwordRegexValidation')

const nameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.nameMinValidation' })
  .regex(nameRegex, 'feedback.validations.nameRegexValidation')

const commentValidation = z
  .string()
  .min(4, { message: 'feedback.validations.commentMinValidation' })
  .max(200, {
    message: 'feedback.validations.commentMaxValidation',
  })

const motivationValidation = z
  .string()
  .min(1, { message: 'feedback.validations.motivationMinValidation' })

const optionalNameValidation = z
  .string()
  .regex(optionalCollageRegex, 'feedback.validations.nameRegexValidation')
  .optional()

const firstAndLastNameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.nameMinValidation' })
  .max(50, { message: 'feedback.validations.firstAndLastNameMaxValidation' })
  .regex(nameRegex, 'feedback.validations.nameRegexValidation')

const middleNameValidation = z
  .string()
  .regex(middleNameRegex, 'feedback.validations.nameRegexValidation')
  .optional()

const companyNameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.companyNameMinValidation' })
  .max(50, { message: 'feedback.validations.companyNameMaxValidation' })
  .regex(alpanumericWithSpaces, 'feedback.validations.nameRegexValidation')

const workspaceNameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.companyNameMinValidation' })
  .max(50, { message: 'feedback.validations.companyNameMaxValidation' })
  .regex(alpanumericWithSpacesAndPoints, {
    message: 'feedback.validations.nameRegexValidation',
  })

const optionalCompanyNameValidation = z
  .string()
  .max(30, { message: 'feedback.validations.companyNameMaxValidation' })
  .regex(alpanumericWithSpaces, 'feedback.validations.nameRegexValidation')
  .optional()

const companyAddressValidation = z
  .string()
  .min(2, { message: 'feedback.validations.companyAddressMinValidation' })
  .max(120, { message: 'feedback.validations.companyAddressMaxValidation' })
  .regex(addressRegex, 'feedback.validations.companyAddressRegexValidation')

const cityValidation = z
  .string()
  .optional()
  .refine((value) => !value || cityRegex.test(value), {
    message: 'feedback.validations.cityRegexValidation',
  })

const countryValidation = z
  .string()
  .min(3, { message: 'feedback.validations.countryMinValidation' })
  .regex(cityRegex, 'feedback.validations.countryRegexValidation')
const optionalCityValidation = z
  .string()
  .regex(cityRegex, 'feedback.validations.cityRegexValidation')
  .optional()

const stateValidation = z
  .string()
  .optional()
  .refine((value) => !value || state.test(value), {
    message: 'feedback.validations.stateRegexValidation',
  })

const optionalStateValidation = z
  .string()
  .regex(state, 'feedback.validations.stateRegexValidation')
  .optional()

const pinValidation = z.string().min(6)

const newSearchPhone = z.preprocess(
  (a) => {
    if (a) {
      return a
    } else {
      return ''
    }
  },
  z.string().regex(phoneOptional, {
    message: 'feedback.validations.newSearchPhoneRegexValidation',
  }),
)

const requiredPhone = z
  .string({ description: 'feedback.validations.requiredPhoneValidation' })
  .min(9)

const optionalEmail = z
  .string()
  .regex(emailRegex, 'feedback.validations.optionalEmailRegexValidation')

const organizationNameValidation = z
  .string()
  .regex(
    organizationName,
    'feedback.validations.organizationNameRegexValidation',
  )
  .min(2, 'feedback.validations.organizationNameMinValidation')
  .max(128, 'feedback.validations.organizationNameMaxValidation')
const optionalWebsiteValidation = z
  .string()
  .regex(websiteRegex, 'feedback.validations.optionalEmailwebsiteValidation')
  .optional()

const linkedinSearchResultsValidation = z
  .string()
  .refine(
    (value) => {
      if (!value) return true
      if (linkedInCompanyUrlRegex.test(value)) {
        return false
      }
      return linkedInUrlRegex.test(value)
    },
    (value) => {
      if (linkedInCompanyUrlRegex.test(value)) {
        return { message: 'feedback.validations.companyLinkedInError' }
      }
      return { message: 'feedback.validations.invalidLinkedIn' }
    },
  )
  .optional()

const imageFileValidation = z
  .instanceof(File)
  .refine((file) => file.type.startsWith('image/'), {
    message: 'feedback.validations.imageFileType',
  })

const imageUrlValidation = z.string().url({
  message: 'feedback.validations.invalidUrl',
})

const imageValidation = z.union([
  imageFileValidation,
  imageUrlValidation,
  z.null(),
])

/**
 * ////////////////////////////////
 * Schemas
 * ////////////////////////////////
 */
export const facebookUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || facebookUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const twitterUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || twitterUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const instagramUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || instagramUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const linkedInUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || linkedInUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const linkedInCompanyUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || linkedInCompanyUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const tikTokUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || tikTokUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const pinterestUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || pinterestUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const redditUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .refine(
    (value) => value === '' || redditUrlRegex.test(value),
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

export const socialMediaRegex = z.union([
  facebookUrlSchema,
  twitterUrlSchema,
  instagramUrlSchema,
  linkedInUrlSchema,
  tikTokUrlSchema,
  pinterestUrlSchema,
  redditUrlSchema,
])

export const loginSchema = z.object({
  email: emailValidation,
  password: passwordValidation,
})

export const contactUsSchema = z.object({
  organizationName: organizationNameValidation,
  name: nameValidation,
  email: emailValidation,
  phoneNumber: newSearchPhone,
  comment: commentValidation,
})

export const letsTalkSchema = z.object({
  name: nameValidation,
  email: emailValidation,
  comment: commentValidation,
})

export const BackgroundCheckContactUsSchema = z.object({
  comment: commentValidation,
})

export const signUpSchema = z.object({
  email: emailValidation,
  createPassword: passwordValidation,
  confirmPassword: passwordValidation,
})

export const signUpFinalStepSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    motivation: motivationValidation,
    customMotivation: z.string().optional(),
    isSSO: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.motivation === 'other') {
        return data.customMotivation && data.customMotivation.trim().length > 0
      }
      return true
    },
    {
      message: 'feedback.validations.motivationMinValidation',
      path: ['customMotivation'],
    },
  )
  .refine(
    (data) => {
      // If not SSO, firstName and lastName are required
      if (!data.isSSO) {
        return (
          data.firstName &&
          data.firstName.trim().length >= 2 &&
          data.lastName &&
          data.lastName.trim().length >= 2
        )
      }
      return true
    },
    {
      message: 'feedback.validations.nameMinValidation',
      path: ['firstName'],
    },
  )

export const inviteMembersSchema = z.object({
  email: emailValidation,
})

const isValidDateRange = (date: string, canPassMinor: boolean) => {
  if (!date || date === '' || (date && date.length < 10)) {
    return true
  }

  const years = differenceInYears(new Date(), new Date(date))

  if (canPassMinor) {
    return years >= 1 && years <= 130
  } else {
    return years >= 18 && years <= 130
  }
}

const isValidDate = (date: string) => {
  if (
    !date ||
    date === '' ||
    (date && date.length < 2) ||
    (date && date.length > 2 && date.length < 10)
  ) {
    return false
  }
  return true
}

const optionalIsValidDate = (date: string | undefined | null) => {
  if (!date) {
    return true
  }
  if (date.length < 2 || (date.length > 2 && date.length < 10)) {
    return false
  }
  return true
}

const isValidNumericAge = (date: string, canPassMinor: boolean) => {
  if (
    !date ||
    date === '' ||
    (date && date.length < 2) ||
    (date && date.length > 2 && date.length <= 10)
  ) {
    return true
  }
  const castedAge = Number(date)
  if (canPassMinor) {
    return castedAge > 9
  } else {
    return castedAge > 17
  }
}

export const dob = z
  .string()
  .refine((data) => isValidDateRange(data, false), {
    message: 'feedback.validations.dobIsValidDateRange',
  })
  .refine((data) => isValidDate(data), {
    message: 'feedback.validations.dobIsValidDate',
  })
  .refine((data) => isValidNumericAge(data, false), {
    message: 'feedback.validations.dobIsValidNumericAge',
  })

export const optionalDob = z
  .string()
  .refine((data) => isValidDateRange(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidDateRange',
  })
  .refine((data) => optionalIsValidDate(data), {
    message: 'feedback.validations.dobWithMinorIsValidDate',
  })
  .refine((data) => isValidNumericAge(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidNumericAge',
  })
  .optional()

export const dobWithMinor = z
  .string()
  .refine((data) => isValidDateRange(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidDateRange',
  })
  .refine((data) => isValidDate(data), {
    message: 'feedback.validations.dobWithMinorIsValidDate',
  })
  .refine((data) => isValidNumericAge(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidNumericAge',
  })

/* const signUpPhoneNumber = z
  .string({ description: 'feedback.validations.signUpPhoneNumber' })
  .min(9, { message: 'feedback.validations.signUpPhoneNumberMin' })
  .max(16, { message: 'feedback.validations.signUpPhoneNumberMax' })
  .regex(signUpPhoneNumberRegex, {
    message: 'feedback.validations.signUpPhoneNumberFormat',
  })
*/

export const newSearchSchema = z.object({
  firstName: firstAndLastNameValidation,
  middleName: optionalNameValidation,
  lastName: firstAndLastNameValidation,
  alias: optionalNameValidation,
  dob: dob,
  addressCity: cityValidation,
  addressCountry: z.optional(countryValidation),
  addressState: z.optional(stateValidation),
  email: z.optional(optionalEmail),
  phoneNumber: newSearchPhone,
  companyName: companyNameValidation,
  collage: optionalNameValidation,
})

export const prefillSearchFormSchema = z
  .object({
    facebookUrl: facebookUrlSchema.optional(),
    instagramUrl: instagramUrlSchema.optional(),
    linkedInUrl: linkedInUrlSchema.optional(),
    tikTokUrl: tikTokUrlSchema.optional(),
    pinterestUrl: pinterestUrlSchema.optional(),
    redditUrl: redditUrlSchema.optional(),
    twitterUrl: twitterUrlSchema.optional(),
  })
  .refine(
    (data) => {
      const socialMediaUrls = [
        data.facebookUrl,
        data.instagramUrl,
        data.linkedInUrl,
        data.tikTokUrl,
        data.pinterestUrl,
        data.redditUrl,
        data.twitterUrl,
      ]
      return socialMediaUrls.some((url) => url && url.trim() !== '')
    },
    {
      message: 'feedback.validations.atLeastOneSocialMediaUrlRequired',
      path: ['socialMedia'],
    },
  )

export const newSearchStep3Schema = z.object({
  middleName: optionalNameValidation,
  alias: optionalNameValidation,
  phoneNumber: newSearchPhone,
  collage: optionalNameValidation,
  addressCity: optionalCityValidation,
  addressState: optionalStateValidation,
  dob: optionalDob,
  companyName: optionalCompanyNameValidation,
})

export const newSearchSchemaWithMinor = z.object({
  firstName: firstAndLastNameValidation,
  middleName: optionalNameValidation,
  lastName: firstAndLastNameValidation,
  alias: optionalNameValidation,
  dob: dobWithMinor,
  addressCity: cityValidation,
  addressCountry: z.optional(countryValidation),
  addressState: z.optional(stateValidation),
  email: z.optional(optionalEmail),
  phoneNumber: newSearchPhone,
  companyName: companyNameValidation,
  collage: optionalNameValidation,
})

export const newQuickSearchSchema = z.object({
  firstName: optionalNameValidation,
  middleName: optionalNameValidation,
  lastName: optionalNameValidation,
  phoneNumber: requiredPhone,
})

export const profileSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  mail: z.string().optional(),
  phoneNumber: z.string().optional(),
})

export const enterpriseSchema = z.object({
  companyName: companyNameValidation,
  companyAddress: companyAddressValidation,
})

export const forgotPasswordSchema = z.object({
  email: emailValidation,
})

export const invitePaswordFlowSchema = z.object({
  createPassword: passwordValidation,
  confirmPassword: passwordValidation,
})

export const inviteNameFlowSchema = z.object({
  firstname: nameValidation,
  lastname: nameValidation,
})

export const createPasswordSchema = z.object({
  createPassword: passwordValidation,
  confirmPassword: passwordValidation,
})

export const emailValidationSchema = z.object({
  pin: pinValidation,
})

export const pDFReportSectionSchema = z.object({
  language: pdfLanguageValidation,
})

export const switchSectionSchema = z.record(z.boolean().optional())

export const workSpaceSchema = z.object({
  workSpaceName: workspaceNameValidation,
})

export const workSpaceThirdStepSchema = z.object({
  email: emailValidation,
})

export const creditsSchema = z.object({
  ai_report: creditsValidation,
  ai_analyst_report: creditsValidation,
  adverse_media_report: creditsValidation,
  assisted_report: creditsValidation,
  facial_matching: creditsValidation,
})

export const renameSchema = z.object({
  name: companyNameValidation,
})

export const companyInfoSchema = z.object({
  companyName: companyNameValidation,
  companyWebsite: optionalWebsiteValidation,
  industry: optionalNameValidation,
  country: countryValidation,
  state: stateValidation,
  city: cityValidation,
})

export const inviteWorkspaceSchema = z.object({
  email: emailValidation,
})

export const searchDetailsSchema = z.object({
  firstName: nameValidation.optional(),
  lastName: nameValidation.optional(),
  school: optionalNameValidation.optional(),
  company: z.string(),
  linkedInUrl: linkedinSearchResultsValidation.optional(),
})

export const inviteMemberValidation = z.object({
  email: emailValidation,
  role: roleValidation,
})

export const manageTeamsFormSchema = z.object({
  teams: z.record(z.array(z.string())),
  role: z.string().optional(),
})

export const signUpPhoneNumberSchema = z.object({
  phoneNumber: z.string().min(9).max(25),
})

export const profileImageSchema = z.object({
  image: imageValidation.optional().nullable(),
})

export const editUserInfoSchema = z.object({
  firstname: firstAndLastNameValidation,
  lastname: firstAndLastNameValidation,
  email: optionalEmail,
})

export const kycContactUsSchema = z.object({
  message: commentValidation,
})

export const motivationSchema = z.object({
  motivation: z.string().optional(),
})

export const updateProfileSchema = z.object({
  firstname: firstAndLastNameValidation.optional(),
  lastname: firstAndLastNameValidation.optional(),
})

export const loadSocialMediaUrlSchema = z.object({
  facebookUrl: facebookUrlSchema.optional(),
  instagramUrl: instagramUrlSchema.optional(),
  linkedInUrl: linkedInUrlSchema.optional(),
  tikTokUrl: tikTokUrlSchema.optional(),
  pinterestUrl: pinterestUrlSchema.optional(),
  redditUrl: redditUrlSchema.optional(),
  twitterUrl: twitterUrlSchema.optional(),
})

export const createProspectSchema = z.object({
  email: emailValidation,
  numberOfSocialAccounts: z
    .number({
      required_error: 'feedback.validations.numberOfSocialAccountsRequired',
      invalid_type_error: 'feedback.validations.numberOfSocialAccountsInvalid',
    })
    .int({ message: 'feedback.validations.numberOfSocialAccountsInteger' })
    .min(1, { message: 'feedback.validations.numberOfSocialAccountsMin' })
    .max(99, { message: 'feedback.validations.numberOfSocialAccountsMax' }),
  termsAccepted: z.literal(true, {
    errorMap: () => ({
      message: 'feedback.validations.termsAcceptedRequired',
    }),
  }),
  privacyPolicyAccepted: z.literal(true, {
    errorMap: () => ({
      message: 'feedback.validations.privacyPolicyAcceptedRequired',
    }),
  }),
})

export type switchSectionType = z.TypeOf<typeof switchSectionSchema>
export type LoginType = z.TypeOf<typeof loginSchema>
export type ContactUsType = z.TypeOf<typeof contactUsSchema>
export type letsTalkType = z.TypeOf<typeof letsTalkSchema>
export type BackgroundCheckContactUsType = z.TypeOf<
  typeof BackgroundCheckContactUsSchema
>
export type NewSearchType = z.TypeOf<typeof newSearchSchema>

export type signUpType = z.TypeOf<typeof signUpSchema>
export type signUpFinalStepType = z.TypeOf<typeof signUpFinalStepSchema>
export type forgotPasswordType = z.TypeOf<typeof forgotPasswordSchema>
export type invitePaswordFlowType = z.TypeOf<typeof invitePaswordFlowSchema>
export type inviteNameFlowType = z.TypeOf<typeof inviteNameFlowSchema>
export type createPasswordType = z.TypeOf<typeof createPasswordSchema>
export type inviteMembersType = z.TypeOf<typeof inviteMembersSchema>
export type emailValidationType = z.TypeOf<typeof emailValidationSchema>
export type profileType = z.TypeOf<typeof profileSchema>
export type enterpriseType = z.TypeOf<typeof enterpriseSchema>
export type searchDetailsSchemaType = z.TypeOf<typeof searchDetailsSchema>
export type prefillSearchFormSchemaTypes = z.TypeOf<
  typeof prefillSearchFormSchema
>

export type newSearchStep3Type = z.TypeOf<typeof newSearchStep3Schema>
export type pDFReportSectionType = z.TypeOf<typeof pDFReportSectionSchema>
export type companyInfoSchemaType = z.TypeOf<typeof companyInfoSchema>
export type workSpaceSchemaType = z.TypeOf<typeof workSpaceSchema>
export type creditsSchemaType = z.TypeOf<typeof creditsSchema>
export type renameSchemaType = z.TypeOf<typeof renameSchema>
export type inviteWorkspaceSchemaType = z.TypeOf<typeof inviteWorkspaceSchema>
export type signUpPhoneNumberType = z.TypeOf<typeof signUpPhoneNumberSchema>

export type workSpaceThirdStepSchemaType = z.TypeOf<
  typeof workSpaceThirdStepSchema
>

export type inviteMemberValidationType = z.TypeOf<typeof inviteMemberValidation>
export type ManageTeamsFormValues = z.infer<typeof manageTeamsFormSchema>

export type ProfileImageSchemaType = z.TypeOf<typeof profileImageSchema>

export type editUserInfoSchemaType = z.TypeOf<typeof editUserInfoSchema>

export type kycContactUsSchemaType = z.TypeOf<typeof kycContactUsSchema>

export type motivationSchemaType = z.TypeOf<typeof motivationSchema>

export type updateProfileSchemaType = z.TypeOf<typeof updateProfileSchema>

export type LoadSocialMediaUrlSchemaType = z.TypeOf<
  typeof loadSocialMediaUrlSchema
>

export type CreateProspectSchemaType = z.TypeOf<typeof createProspectSchema>

export {
  emailValidation,
  firstAndLastNameValidation,
  middleNameValidation,
  optionalNameValidation,
  countryValidation,
  stateValidation,
  cityValidation,
  newSearchPhone,
}
